import _ from 'lodash';
import { ExceptionStatus } from '@amzn/aet-dynamo-kit';
import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import { RawOutlier } from '../../api/Outlier/schema';
import {
  OutlierCategory,
  FilterStatusType,
  OutlierType,
  UniversalFilterValue,
  OutlierMetric,
  OutlierMetricsData,
  AssociatesMetricsData,
  DisplayMessage,
  OMOutlierSummaryStats,
  OutlierException,
  OutlierContact,
  OMExceptionMetricsWithDetails,
  RequiredReviewsData,
  AssociateRequiredReviews,
} from './types/types';
import {
  ZERO_TOLERANCE_PRIORITY_BOOST,
  BACKEND_CATEGORY_MAP,
  NUM_WEEKS_FOR_OUTLIERS,
} from './types/constants';
import moment from 'moment';
import { getAmazonWeekAsGGGGDashWW } from '@amzn/aet-generic-kit';
import { ExtendedUser, TeamProfile, ReportingTeamUser } from '../../Types';

export function isPendingContact(status: ContactStatusOptions): boolean {
  return (
    status === null ||
    status === ContactStatusOptions.UNDEFINED ||
    status === ContactStatusOptions.DRAFT
  );
}

export function isCompletedContact(status: ContactStatusOptions): boolean {
  return (
    status === ContactStatusOptions.REVIEWED ||
    status === ContactStatusOptions.DELIVERED
  );
}

const getOutliersCountByCondition = (
  categories: OutlierCategory[],
  condition: (status: ContactStatusOptions) => boolean
): number => {
  return categories.reduce((total, category) => {
    return (
      total +
      category.contacts.filter(contact => condition(contact.status)).length
    );
  }, 0);
};

export const getTotalOutliers = (
  categories: OutlierCategory[],
  status: FilterStatusType
): number => {
  return getOutliersCountByCondition(categories, contactStatus => {
    if (status === UniversalFilterValue.ALL) return true;
    if (isPendingContact(status)) return isPendingContact(contactStatus);
    return contactStatus === status;
  });
};

export const getPendingOutliersCount = (
  categories: OutlierCategory[]
): number => {
  return getOutliersCountByCondition(categories, isPendingContact);
};

export const getReviewedOutliersCount = (
  categories: OutlierCategory[]
): number => {
  return getOutliersCountByCondition(categories, isCompletedContact);
};

export const getUniqueAssociatesCount = (
  categories: OutlierCategory[]
): number => {
  const uniqueAssociates = new Set<string>();
  categories.forEach(category => {
    category.contacts
      .filter(contact => isPendingContact(contact.status))
      .forEach(contact => uniqueAssociates.add(contact.user.login));
  });
  return uniqueAssociates.size;
};

const getAssociatesByCategory = (
  categories: OutlierCategory[]
): OutlierMetric[] => {
  const categoryAssociates = new Map<OutlierType, Set<string>>();

  categories.forEach(category => {
    const associates = new Set<string>();
    category.contacts
      .filter(contact => isPendingContact(contact.status))
      .forEach(contact => associates.add(contact.user.login));

    if (associates.size > 0) {
      categoryAssociates.set(category.title, associates);
    }
  });

  return Array.from(categoryAssociates.entries())
    .map(([type, associates]) => ({
      type,
      count: associates.size,
    }))
    .sort((a, b) => b.count - a.count);
};

export const getZeroToleranceCategories = (
  categories: OutlierCategory[]
): OutlierCategory[] => {
  return categories.filter(category => isZeroToleranceOutlier(category.title));
};

export const hasZeroToleranceViolations = (
  category: OutlierCategory
): boolean => {
  if (!isZeroToleranceOutlier(category.title)) return false;
  return getOutliersCountByCondition([category], isPendingContact) > 0;
};

export const getStatusText = (status: FilterStatusType): DisplayMessage => {
  switch (status) {
    case ContactStatusOptions.UNDEFINED:
      return {
        id: 'cs_perfportal_status_pending',
        defaultMessage: 'Pending',
      };
    case ContactStatusOptions.DELIVERED:
      return {
        id: 'cs_perfportal_status_delivered',
        defaultMessage: 'Delivered',
      };
    case ContactStatusOptions.REVIEWED:
      return {
        id: 'cs_perfportal_status_reviewed',
        defaultMessage: 'Reviewed',
      };
    case ContactStatusOptions.DRAFT:
      return {
        id: 'cs_perfportal_status_draft',
        defaultMessage: 'Draft',
      };
    case UniversalFilterValue.ALL:
      return {
        id: 'cs_perfportal_status_total',
        defaultMessage: 'Total',
      };
    default:
      return {
        id: 'cs_perfportal_status_total',
        defaultMessage: 'Total',
      };
  }
};

export const transformRawStatus = (
  status: string | null
): ContactStatusOptions => {
  if (status === null) return ContactStatusOptions.UNDEFINED;

  switch (status) {
    case 'DRAFT':
      return ContactStatusOptions.DRAFT;
    case 'DELIVERED':
      return ContactStatusOptions.DELIVERED;
    case 'REVIEWED':
      return ContactStatusOptions.REVIEWED;
    default:
      return ContactStatusOptions.UNDEFINED;
  }
};

export const transformOutliersToCategories = (
  outliers: RawOutlier[],
  includeExceptions: boolean = false,
  csaToTMAliasMap?: Record<string, string>
): OutlierCategory[] => {
  const categoriesMap = new Map<OutlierType, OutlierCategory>();

  outliers.forEach(outlier => {
    const definition = BACKEND_CATEGORY_MAP[outlier.category];
    if (!definition) {
      console.warn(`Unknown category: ${outlier.category}`);
      return;
    }

    const frontendCategory = definition.value;

    if (!categoriesMap.has(frontendCategory)) {
      categoriesMap.set(frontendCategory, {
        title: frontendCategory,
        contacts: [],
      });
    }

    const category = categoriesMap.get(frontendCategory)!;

    outlier.relatedContacts.forEach(contact => {
      const status =
        contact.status === null
          ? ContactStatusOptions.UNDEFINED
          : transformRawStatus(contact.status);

      const userID = outlier.userID.includes(':')
        ? outlier.userID.split(':')[1]
        : outlier.userID;

      const maybeTMUser = _.get(csaToTMAliasMap, outlier.userName);

      const contactObj: OutlierContact = {
        contactId: contact.contactID,
        status,
        repeatedIssue: outlier.isRepeat,
        week: getAmazonWeekAsGGGGDashWW(moment()),
        user: {
          id: userID,
          login: outlier.userName,
        },
        ...(maybeTMUser
          ? {
              tmUser: {
                login: maybeTMUser,
              },
            }
          : {}),
        category: frontendCategory,
        justification: contact.justification || null,
      };

      // Only add exception information for OM view
      if (includeExceptions) {
        contactObj.exception = {
          status: contact.exceptionStatus || null,
        };
      }

      category.contacts.push(contactObj);
    });
  });

  return Array.from(categoriesMap.values());
};

export const getCategoryPriority = (category: OutlierCategory): number => {
  const isZeroTolerance = isZeroToleranceOutlier(category.title);
  const pendingCount = getOutliersCountByCondition(
    [category],
    isPendingContact
  );
  return (isZeroTolerance ? ZERO_TOLERANCE_PRIORITY_BOOST : 0) + pendingCount;
};

export const sortCategories = (
  categories: OutlierCategory[],
  status: FilterStatusType = UniversalFilterValue.ALL
): OutlierCategory[] => {
  return [...categories].sort((a, b) => {
    const aIsZeroTolerance = isZeroToleranceOutlier(a.title);
    const bIsZeroTolerance = isZeroToleranceOutlier(b.title);

    if (aIsZeroTolerance !== bIsZeroTolerance) {
      return aIsZeroTolerance ? -1 : 1;
    }

    const aCount = getTotalOutliers([a], status);
    const bCount = getTotalOutliers([b], status);

    if (aCount !== bCount) {
      return bCount - aCount;
    }

    const aPendingCount = getOutliersCountByCondition([a], isPendingContact);
    const bPendingCount = getOutliersCountByCondition([b], isPendingContact);

    if (aPendingCount !== bPendingCount) {
      return bPendingCount - aPendingCount;
    }

    return a.title.localeCompare(b.title);
  });
};

export const getOutlierTypesByStatus = (
  categories: OutlierCategory[],
  isPending: boolean,
  requiredReviewsByCategory?: Record<string, RequiredReviewsData>
): OutlierMetric[] => {
  const typeCounts = new Map<OutlierType, number>();

  categories.forEach(category => {
    const count = getOutliersCountByCondition(
      [category],
      isPending ? isPendingContact : isCompletedContact
    );

    if (count > 0) {
      typeCounts.set(category.title, count);
    }
  });

  return Array.from(typeCounts.entries())
    .map(([type, count]) => {
      const metric: OutlierMetric = { type, count };

      // Add required reviews remaining if available
      if (requiredReviewsByCategory && requiredReviewsByCategory[type]) {
        metric.requiredReviewsRemaining =
          requiredReviewsByCategory[type].remainingRequired;
      }

      return metric;
    })
    .sort((a, b) => {
      // Sort by required reviews remaining first if available
      if (
        a.requiredReviewsRemaining !== undefined &&
        b.requiredReviewsRemaining !== undefined
      ) {
        if (a.requiredReviewsRemaining !== b.requiredReviewsRemaining) {
          return b.requiredReviewsRemaining - a.requiredReviewsRemaining;
        }
      }
      // Fall back to count-based sorting
      return b.count - a.count;
    });
};

/**
 * Get the required number of reviews for a category/associate
 * For zero tolerance categories, only 1 review is required
 * For non-zero tolerance categories, 2 reviews are required
 * If there's only 1 contact available for a CSA in a non-zero-tolerance category,
 * only 1 review is required
 */
export const getRequiredReviewCount = (
  categoryType: OutlierType,
  isZeroTolerance: boolean,
  availableContactsCount: number
): number => {
  const standardRequirement = isZeroTolerance ? 1 : 2;
  return Math.min(standardRequirement, availableContactsCount);
};

/**
 * Calculate required reviews data for all categories and associates
 * @param categories - The outlier categories to calculate required reviews for
 * @param validCSALogins - Optional set of CSA logins to filter by. Only contacts for CSAs in this set
 *                         will be counted toward required reviews. If not provided, all contacts are counted.
 */
export const calculateRequiredReviewsData = (
  categories: OutlierCategory[],
  validCSALogins?: Set<string>
): {
  total: RequiredReviewsData;
  byAssociate: AssociateRequiredReviews[];
  byCategory: Record<string, RequiredReviewsData>;
} => {
  // Map to track required reviews by associate and category
  const associateMap = new Map<
    string,
    {
      id: string;
      login: string;
      requiredReviews: RequiredReviewsData;
      categories: Record<string, RequiredReviewsData>;
    }
  >();

  // Map to track required reviews by category
  const categoryMap = new Map<string, RequiredReviewsData>();

  // Total required reviews across all associates
  const totalRequired: RequiredReviewsData = {
    totalRequired: 0,
    remainingRequired: 0,
    completedRequired: 0,
  };

  // Process each category
  categories.forEach(category => {
    const isZeroTolerance = isZeroToleranceOutlier(category.title);
    const categoryType = category.title;

    // Initialize category data if not exists
    if (!categoryMap.has(categoryType)) {
      categoryMap.set(categoryType, {
        totalRequired: 0,
        remainingRequired: 0,
        completedRequired: 0,
      });
    }

    // Get category data
    const categoryData = categoryMap.get(categoryType)!;

    // Filter contacts by valid CSA logins if provided
    const filteredContacts = validCSALogins
      ? category.contacts.filter(contact =>
          validCSALogins.has(contact.user.login)
        )
      : category.contacts;

    // Group contacts by associate
    const contactsByAssociate = _.groupBy(
      filteredContacts,
      contact => contact.user.login
    );

    // Process each associate's contacts for this category
    Object.entries(contactsByAssociate).forEach(([login, contacts]) => {
      if (contacts.length === 0) return;

      // Get or create associate entry
      const associate = associateMap.get(login) || {
        id: contacts[0].user.id,
        login,
        requiredReviews: {
          totalRequired: 0,
          remainingRequired: 0,
          completedRequired: 0,
        },
        categories: {},
      };

      // Calculate required reviews for this associate and category
      const requiredCount = getRequiredReviewCount(
        category.title,
        isZeroTolerance,
        contacts.length
      );
      const completedCount = contacts.filter(contact =>
        isCompletedContact(contact.status)
      ).length;
      const completedRequired = Math.min(completedCount, requiredCount);
      const remainingRequired = Math.max(0, requiredCount - completedRequired);

      // Create or update category data for this associate
      associate.categories[category.title] = {
        totalRequired: requiredCount,
        completedRequired,
        remainingRequired,
      };

      // Update associate totals
      associate.requiredReviews.totalRequired += requiredCount;
      associate.requiredReviews.completedRequired += completedRequired;
      associate.requiredReviews.remainingRequired += remainingRequired;

      // Update category totals
      categoryData.totalRequired += requiredCount;
      categoryData.completedRequired += completedRequired;
      categoryData.remainingRequired += remainingRequired;

      // Update global totals
      totalRequired.totalRequired += requiredCount;
      totalRequired.completedRequired += completedRequired;
      totalRequired.remainingRequired += remainingRequired;

      // Store updated associate data
      associateMap.set(login, associate);
    });
  });

  // Convert map to array and sort by remaining required reviews (descending)
  const byAssociate = Array.from(associateMap.values())
    .filter(associate => associate.requiredReviews.remainingRequired > 0)
    .sort(
      (a, b) =>
        b.requiredReviews.remainingRequired -
        a.requiredReviews.remainingRequired
    );

  // Convert category map to record
  const byCategory: Record<string, RequiredReviewsData> = {};
  categoryMap.forEach((data, category) => {
    byCategory[category] = data;
  });

  return { total: totalRequired, byAssociate, byCategory };
};

export const calculateMetricsData = (
  categories: OutlierCategory[],
  validCSALogins: Set<string>
): OutlierMetricsData => {
  const pendingOutliers = getPendingOutliersCount(categories);
  const reviewedCount = getReviewedOutliersCount(categories);
  const totalCount = pendingOutliers + reviewedCount;
  const reviewedPercentage =
    totalCount > 0 ? Math.round((reviewedCount / totalCount) * 100) : 0;

  // Calculate required reviews data filtered by valid CSA logins
  const requiredReviewsData = calculateRequiredReviewsData(
    categories,
    validCSALogins
  );

  return {
    pendingOutliers,
    reviewedCount,
    totalCount,
    pendingOutlierTypes: getOutlierTypesByStatus(
      categories,
      true,
      requiredReviewsData.byCategory
    ),
    reviewMetrics: {
      count: reviewedCount,
      percentage: reviewedPercentage,
    },
    requiredReviews: requiredReviewsData.total,
  };
};

export const calculateAssociatesData = (
  categories: OutlierCategory[],
  validCSALogins: Set<string>
): AssociatesMetricsData => {
  // Calculate required reviews data for associates filtered by valid CSA logins
  const requiredReviewsData = calculateRequiredReviewsData(
    categories,
    validCSALogins
  );

  return {
    totalAssociates: getUniqueAssociatesCount(categories),
    pendingAssociateTypes: getAssociatesByCategory(categories),
    associatesWithRequiredReviews: requiredReviewsData.byAssociate,
  };
};

export const getOutlierDefinition = (value: OutlierType) => {
  return BACKEND_CATEGORY_MAP[
    Object.keys(BACKEND_CATEGORY_MAP).find(
      key => BACKEND_CATEGORY_MAP[key].value === value
    )!
  ];
};

export const getOutlierI18nKey = (type: OutlierType): string => {
  return getOutlierDefinition(type).i18nKey;
};

export const isZeroToleranceOutlier = (type: OutlierType): boolean => {
  return getOutlierDefinition(type).isZeroTolerance;
};

export const getOutlierWeeksList = (): string[] => {
  const now = moment().utc().add(1, 'day');

  return Array.from({ length: NUM_WEEKS_FOR_OUTLIERS }, (_, index) => {
    return moment(now).subtract(index, 'weeks').format('GGGG-WW');
  });
};

export const getPastWeek = (): string => {
  return moment().utc().add(1, 'day').subtract(1, 'weeks').format('GGGG-WW');
};

export const isContactFlaggedInvalid = (contact: OutlierContact) => {
  // Basically if there is a non-null/truthy exception status, it means it was flagged by TM
  // We don't care whether OM has approved it or not to consider it as "flagged" contact.
  return !!contact.exception?.status;
};

export const calculateOMMetrics = (
  categories: OutlierCategory[],
  csaToTMAliasMap: Record<string, string>,
  currentDirectReportsOfOM: ExtendedUser[],
  currentCSAToTMMap?: Record<string, string>
) => {
  const tmStats = new Map<string, OMOutlierSummaryStats>();

  // First, collect all contacts by TM
  const contactsByTM = new Map<string, OutlierContact[]>();

  categories.forEach(category => {
    category.contacts.forEach(contact => {
      const csaAlias = contact.user.login;
      const tmAlias = csaToTMAliasMap[csaAlias];

      if (!tmAlias) {
        console.warn(`No TM info found for CSA: ${csaAlias}`);
        return;
      }

      // Initialize contacts array for this TM if it doesn't exist
      if (!contactsByTM.has(tmAlias)) {
        contactsByTM.set(tmAlias, []);
      }

      // Add this contact to the TM's contacts
      contactsByTM.get(tmAlias)!.push(contact);
    });
  });

  // Process each TM's contacts
  contactsByTM.forEach((contacts, tmAlias) => {
    const firstContact = contacts[0];
    const csaAlias = firstContact.user.login;
    const tmInfo = csaToTMAliasMap[csaAlias];

    // Initialize TM stats
    const tmStat: OMOutlierSummaryStats = {
      tmAlias,
      numAssociates: Object.values(csaToTMAliasMap).filter(
        managerName => managerName === tmInfo
      ).length,
      numInvalidFlags: 0,
      totalOutliers: contacts.length,
      pendingOutliers: contacts.filter(contact =>
        isPendingContact(contact.status)
      ).length,
      hasRepeatOutliers: contacts.some(contact => contact.repeatedIssue),
      requiredRemaining: 0,
      totalRequired: 0,
      tmId: getTmIdFromProfileData(tmAlias, currentDirectReportsOfOM),
      tmDisplayName: getTmDisplayNameFromProfileData(
        tmAlias,
        currentDirectReportsOfOM
      ),
    };

    // Count invalid flags
    tmStat.numInvalidFlags = contacts.filter(contact =>
      isContactFlaggedInvalid(contact)
    ).length;

    // Filter contacts to only those where CSA currently reports to this TM
    // This ensures we don't count contacts for CSAs who have transferred or left Amazon
    const validContacts = currentCSAToTMMap
      ? contacts.filter(contact => {
          const currentTM = currentCSAToTMMap[contact.user.login];
          return currentTM === tmAlias;
        })
      : contacts;

    // Group contacts by CSA and category to calculate required reviews
    const contactsByCSAAndCategory = _.groupBy(
      validContacts,
      contact => `${contact.user.login}:${contact.category}`
    );

    // Calculate required reviews for each CSA and category
    Object.entries(contactsByCSAAndCategory).forEach(
      ([key, csaCategoryContacts]) => {
        const [csaAlias, categoryName] = key.split(':');
        const category = categories.find(cat => cat.title === categoryName);

        if (!category) return;

        const isZeroTolerance = isZeroToleranceOutlier(category.title);
        const requiredCount = getRequiredReviewCount(
          category.title,
          isZeroTolerance,
          csaCategoryContacts.length
        );
        const completedCount = csaCategoryContacts.filter(contact =>
          isCompletedContact(contact.status)
        ).length;

        const completedRequired = Math.min(completedCount, requiredCount);
        const remainingRequired = Math.max(
          0,
          requiredCount - completedRequired
        );

        tmStat.totalRequired += requiredCount;
        tmStat.requiredRemaining += remainingRequired;
      }
    );

    tmStats.set(tmAlias, tmStat);
  });

  return Array.from(tmStats.values());
};

export const calculateExceptionMetrics = (
  categories: OutlierCategory[],
  csaToTMAliasMap: Record<string, string>,
  omUserIdWithPrefix: string,
  cscEntityPrefix: string
): OMExceptionMetricsWithDetails => {
  const exceptions: OutlierException[] = [];
  let totalExceptions = 0;
  let pendingExceptions = 0;
  let acceptedExceptions = 0;
  let deniedExceptions = 0;

  categories.forEach(category => {
    category.contacts.forEach(contact => {
      if (contact.exception && isContactFlaggedInvalid(contact)) {
        totalExceptions++;

        const csaAlias = contact.user.login;
        const tmAlias = csaToTMAliasMap[csaAlias];
        const csaUserIdWithPrefix = `${cscEntityPrefix}:${contact.user.id}`;
        const year = contact.week.split('-')[0];

        switch (contact.exception.status) {
          case ExceptionStatus.PENDING:
            pendingExceptions++;
            break;
          case ExceptionStatus.ACCEPTED:
            acceptedExceptions++;
            break;
          case ExceptionStatus.DENIED:
            deniedExceptions++;
            break;
        }

        const exception: OutlierException = {
          exceptionId: `${contact.contactId}-${contact.user.id}-${year}`,
          contactId: contact.contactId,
          csaUserIdWithPrefix,
          omUserIdWithPrefix,
          tmAlias,
          csaAlias,
          category: category.title,
          status: contact.exception.status,
          outlierCalYear: year,
          justification: contact.justification || null,
        };

        exceptions.push(exception);
      }
    });
  });

  return {
    exceptions,
    metrics: {
      totalExceptions,
      pendingExceptions,
      acceptedExceptions,
      deniedExceptions,
    },
  };
};

export const buildCSAToTMAliasMap = (
  outliers: RawOutlier[]
): Record<string, string> => {
  const map: Record<string, string> = {};

  for (const outlier of outliers) {
    map[outlier.userName] = outlier.managerUserName;
  }

  return map;
};

export function getTmIdFromProfileData(
  tmAlias: string,
  directReportsOfOM: ExtendedUser[]
): string | undefined {
  const matchingUser = directReportsOfOM.find(user => user.alias === tmAlias);
  return matchingUser?.userID;
}

export function getTmDisplayNameFromProfileData(
  tmAlias: string,
  directReportsOfOM: ExtendedUser[]
): string | undefined {
  const matchingUser = directReportsOfOM.find(user => user.alias === tmAlias);
  return matchingUser?.displayName;
}

/**
 * Build a map of CSA alias to TM alias using the current reporting structure from TeamProfile.
 * This is used to determine which CSAs currently report to which TMs, filtering out stale
 * relationships where a CSA may have transferred to a different manager.
 *
 * @param teamProfile - The TeamProfile containing the full reporting team structure
 * @returns Record<csaAlias, tmAlias> mapping each CSA to their current TM
 */
export function buildCurrentCSAToTMMap(
  teamProfile: TeamProfile
): Record<string, string> {
  const csaToTMMap: Record<string, string> = {};

  // Get all TMs from the reporting team
  const tms = teamProfile.reportingTeam.filter(
    (member: ReportingTeamUser) => member.isManager === true
  );

  // For each TM, map their subReports (which are CSA aliases) to the TM's alias
  // Note: subReports contains aliases directly, not userIDs
  tms.forEach((tm: ReportingTeamUser) => {
    const tmAlias = tm.relatedUser.alias;
    const subReports = tm.subReports || [];

    subReports.forEach((csaAlias: string) => {
      csaToTMMap[csaAlias] = tmAlias;
    });
  });

  return csaToTMMap;
}
